/*
 * ******************************************************************
 * Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */

import { Injectable } from "@angular/core";
import { CspEnvironment } from "@vmw/csp-ngx-components";
import { OAuth2ModuleConfiguration } from "@vmw/ngx-csp-oauth2";
import { AppConfig, Feature, ServiceConfig, ServiceState } from "@app/services/app-config/model/app-config";
import { environment } from "src/environments/environment";
import { VLR_CONSOLE } from "@vlr-console/vlr-console-routing-util";
import { OPERATOR_CONSOLE } from "@op-console/op-console-routing-util";
import { VMCFS_CONSOLE } from "@vmcfs-console/vmcfs-console-routing-util";
import { APP_ROUTES_UTIL } from "@app/app-routing.util";
import { ConsoleType } from "@shared/models/global.model";
import { ServiceTypeEnum } from "@api/generated/vcdr";
import { ConsoleStrings, OPERATOR_STRINGS, QUORUM_STRINGS, VLR_STRINGS, VMCFS_STRINGS } from "@shared/models/strings.model";
import { QUORUM_CONSOLE } from "@app/modules/quorum-console/quorum-console-routing-util";

export enum AUTH_KEYS {
    ORG_ID = "AUTH_KEYS_ORG_ID",
    CLIENT_TYPE = "AUTH_KEYS_CLIENT_TYPE",
    CLIENT_TYPE_FROM_PATH = "AUTH_KEYS_CLIENT_TYPE_FROM_PATH",
    PAIRING_ORD_ID = "PAIRING_ORD_ID"
}

export enum URL_PARAMS {
    PAIRING_USER_CODE = "user_code"
}

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class AppConfigService implements AppConfig {

    private _clientType: ConsoleType;
    private urlParamCache: Map<string, string> = new Map<string, string>();

    constructor() {
        this.loadParam(URL_PARAMS.PAIRING_USER_CODE, true);
    }

    get production(): boolean {
        return environment.production;
    }

    get vipHost(): string {
        return environment.vipHost;
    }

    get opConsoleApiServer(): string {
        return environment.opConsoleApiServer;
    }

    get opConsoleOrgId(): string {
        return environment.opConsoleOrgId;
    }

    get opConsoleClientId(): string {
        return environment.opConsoleClientId;
    }

    get opConsoleServiceId(): string {
        return environment.opConsoleServiceId;
    }

    get selfServiceApiServer(): string {
        return environment.selfServiceApiServer;
    }

    get services(): Map<ServiceTypeEnum, ServiceConfig> {
        return environment.services;
    }

    getService(serviceType: ServiceTypeEnum): ServiceConfig {
        return environment.services.get(serviceType);
    }

    getServiceId(): string {
        let serviceId: string;
        if (this.clientType === ConsoleType.OPERATOR) {
            serviceId = this.opConsoleServiceId;
        } else if (this.clientType === ConsoleType.VLR) {
            const serviceConfig = this.getService(ServiceTypeEnum.VCDR);
            serviceId = serviceConfig.serviceId;
        } else if (this.clientType === ConsoleType.VMCFS) {
            const serviceConfig = this.getService(ServiceTypeEnum.VMCFS);
            serviceId = serviceConfig.serviceId;
        } else if (this.clientType === ConsoleType.QUORUM) {
            const serviceConfig = this.getService(ServiceTypeEnum.VMUA);
            serviceId = serviceConfig.serviceId;
        }
        return serviceId;
    }

    getServiceName(): string {
        let serviceName: string;
        if (this.clientType === ConsoleType.OPERATOR) {
            serviceName = OPERATOR_STRINGS.name;
        } else if (this.clientType === ConsoleType.VLR) {
            serviceName = VLR_STRINGS.name;
        } else if (this.clientType === ConsoleType.VMCFS) {
            serviceName = VMCFS_STRINGS.name;
        } else if (this.clientType === ConsoleType.QUORUM) {
            serviceName = QUORUM_STRINGS.name;
        }
        return serviceName;
    }

    getConsoleStrings(): ConsoleStrings {
        let consoleStrings: ConsoleStrings = new ConsoleStrings();
        if (this.clientType === ConsoleType.OPERATOR) {
            consoleStrings = OPERATOR_STRINGS;
        } else if (this.clientType === ConsoleType.VLR) {
            consoleStrings = VLR_STRINGS;
        } else if (this.clientType === ConsoleType.VMCFS) {
            consoleStrings = VMCFS_STRINGS;
        } else if (this.clientType === ConsoleType.QUORUM) {
            consoleStrings = QUORUM_STRINGS;
        }
        return consoleStrings;
    }

    isServiceEnabled(serviceType: ServiceTypeEnum): boolean {
        const serviceConfig = this.getService(serviceType);
        if (!serviceConfig) {
            return false;
        }
        return serviceConfig.serviceState > ServiceState.DISABLED;
    }

    isFeatureEnabled(feature: Feature, serviceType?: ServiceTypeEnum): boolean {
        let featureEnabled = false;
        const consoleType = this.getClientType();
        environment.services.forEach((serviceConfig, service) => {
            if (!serviceConfig) {
                return;
            }

            if (consoleType === serviceConfig.selfServiceConsole ||
                (consoleType === ConsoleType.OPERATOR && service === serviceType)) {
                if (serviceConfig.features?.find(feat => feat.equalTo(feature))) {
                    featureEnabled = true;
                }
            }
        });
        return featureEnabled;
    }

    public getParam(param: string): string {
        return this.urlParamCache.get(param);
    }

    private loadParam(param: string, skipDecoding: boolean = false): void {
        let urlParam: string;

        // Standard parameter parsing is done using URLSearchParams which does decoding by default.
        // This is not suitable for base64 encoded parameters. For those, perform manual parsing.
        if (skipDecoding) {
            const urlParams: string[] = window.location.search.split("&");
            const arg: string = urlParams.find(a => a?.includes(param));
            if (arg) {
                const valueIndex: number = arg.indexOf('=');
                if (valueIndex >= 0) {
                    urlParam = arg.substring(valueIndex + 1);
                }
            }
        } else {
            const urlParams = new URLSearchParams(window.location.search);
            urlParam = urlParams.get(param);
        }
        if (urlParam) {
            window.sessionStorage.setItem(param, urlParam);
            this.urlParamCache.set(param, urlParam);
        } else {
            urlParam = window.sessionStorage.getItem(param);
            if (urlParam) {
                this.urlParamCache.set(param, urlParam);
            }
        }
    }

    get cspServiceId(): string {
        return environment.cspServiceId;
    }

    get cspEnvironment(): CspEnvironment {
        return environment.cspEnvironment;
    }

    get cspOAuthConfig(): Partial<OAuth2ModuleConfiguration> {
        const retval: Partial<OAuth2ModuleConfiguration> = environment.cspOAuthConfig;

        // Set correct client ID based on client type.
        if (this.clientType === ConsoleType.OPERATOR) {
            retval.clientId = environment.opConsoleClientId;
        } else if (this.clientType === ConsoleType.VLR) {
            const vcdrService: ServiceConfig = this.getService(ServiceTypeEnum.VCDR);
            retval.clientId = vcdrService.clientId;
        } else if (this.clientType === ConsoleType.VMCFS) {
            const vmcfs: ServiceConfig = this.getService(ServiceTypeEnum.VMCFS);
            retval.clientId = vmcfs.clientId;
        } else if (this.clientType === ConsoleType.QUORUM) {
            const quorumService: ServiceConfig = this.getService(ServiceTypeEnum.VMUA);
            retval.clientId = quorumService.clientId;
        }

        return retval;
    }

    get redirectPaths(): Map<string, string> {
        return environment.redirectPaths;
    }

    get clientType(): ConsoleType {
        if (!this._clientType) {
            this._clientType = this.getClientType();
        }
        return this._clientType;
    }

    getUserLink(): string {
        const cspHostName: string = this.cspOAuthConfig?.cspHostname;
        if (this.redirectPaths) {
            return cspHostName + "/identity-access-management/users";
        }
        return cspHostName + "/csp/gateway/portal/#/consumer/usermgmt/users";
    }

    // Determine client type from org ID, path, or saved client type.
    private getClientType(): ConsoleType {
        let clientType: ConsoleType;

        const orgId = window.sessionStorage.getItem(AUTH_KEYS.ORG_ID);
        if (orgId === environment.opConsoleOrgId) {
            // If current org is the Operator org, change the client ID.
            clientType = ConsoleType.OPERATOR;
        } else {
            const path: string = window.location.pathname;
            if (path === `${APP_ROUTES_UTIL.BASE_PATH}${VLR_CONSOLE.ROOT}` ||
                path.startsWith(VLR_CONSOLE.ROOT_ABSOLUTE)) {
                clientType = ConsoleType.VLR;
                window.sessionStorage.setItem(AUTH_KEYS.CLIENT_TYPE_FROM_PATH, clientType.toString());
            } else if (path === `${APP_ROUTES_UTIL.BASE_PATH}${OPERATOR_CONSOLE.ROOT}` ||
                path.startsWith(OPERATOR_CONSOLE.ROOT_ABSOLUTE)) {
                clientType = ConsoleType.OPERATOR;
                window.sessionStorage.setItem(AUTH_KEYS.CLIENT_TYPE_FROM_PATH, clientType.toString());
            } else if (path === `${APP_ROUTES_UTIL.BASE_PATH}${VMCFS_CONSOLE.ROOT}` ||
                path.startsWith(VMCFS_CONSOLE.ROOT_ABSOLUTE)) {
                clientType = ConsoleType.VMCFS;
                window.sessionStorage.setItem(AUTH_KEYS.CLIENT_TYPE_FROM_PATH, clientType.toString());
            } else if (path === `${APP_ROUTES_UTIL.BASE_PATH}${QUORUM_CONSOLE.ROOT}` ||
                path.startsWith(QUORUM_CONSOLE.ROOT_ABSOLUTE)) {
                clientType = ConsoleType.QUORUM;
                window.sessionStorage.setItem(AUTH_KEYS.CLIENT_TYPE_FROM_PATH, clientType.toString());
            } else {
                // If CSP authorization URI or an invalid path, retrieve saved client type.
                clientType = <ConsoleType>window.sessionStorage.getItem(AUTH_KEYS.CLIENT_TYPE);

                // Special handling when switching from the operator org to a non-operator org.
                const clientTypeFromPath: ConsoleType =
                    <ConsoleType>window.sessionStorage.getItem(AUTH_KEYS.CLIENT_TYPE_FROM_PATH);
                if (clientType === ConsoleType.OPERATOR && clientTypeFromPath !== ConsoleType.OPERATOR) {
                    // Default to the VCDR console type when changing from an operator org to a non-operator org.
                    clientType = ConsoleType.VLR;
                }
            }
        }

        // Set client type.
        if (clientType) {
            window.sessionStorage.setItem(AUTH_KEYS.CLIENT_TYPE, clientType.toString());
        }

        // Return client type.
        return clientType;
    }
}
